import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Droplets, Heart, ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const HowItWorks = () => {
  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Comment ça marche"
        description="Découvrez le modèle publicitaire innovant d'Eau Gratuite : financez votre campagne, distribuez des bouteilles gratuites en Île-de-France, et générez un impact social."
        path="/comment-ca-marche"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {mockData.howItWorks.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {mockData.howItWorks.subtitle}
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mockData.howItWorks.steps.map((step, index) => {
              const IconComponent = {
                building: Building2,
                droplets: Droplets,
                heart: Heart
              }[step.icon];
              
              const isEven = index % 2 === 0;
              
              return (
                <div key={step.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`space-y-6 ${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex items-center justify-center w-12 h-12 bg-sky-100 rounded-full">
                        <span className="text-xl font-bold text-sky-600">{step.id}</span>
                      </div>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{step.title}</h2>
                    <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
                    
                    {/* Additional details for each step */}
                    {step.id === 1 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-gray-900">Nos partenaires s'engagent pour :</h3>
                        <div className="space-y-2">
                          {['Impact RSE mesurable', 'Visibilité auprès des consommateurs', 'Communication positive'].map((benefit) => (
                            <div key={benefit} className="flex items-center space-x-3">
                              <CheckCircle className="h-5 w-5 text-emerald-500" />
                              <span className="text-gray-600">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {step.id === 2 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-gray-900">Les consommateurs bénéficient de :</h3>
                        <div className="space-y-2">
                          {['Eau de qualité premium gratuite', 'QR code pour accéder à vos offres', 'Interaction directe avec votre marque'].map((benefit) => (
                            <div key={benefit} className="flex items-center space-x-3">
                              <CheckCircle className="h-5 w-5 text-sky-500" />
                              <span className="text-gray-600">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {step.id === 3 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-gray-900">Impact de votre consommation :</h3>
                        <div className="space-y-2">
                          {['Construction de puits durables', 'Accès à l\'eau potable', 'Suivi transparent des projets'].map((benefit) => (
                            <div key={benefit} className="flex items-center space-x-3">
                              <CheckCircle className="h-5 w-5 text-emerald-500" />
                              <span className="text-gray-600">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className={`relative ${!isEven ? 'lg:order-1' : ''}`}>
                    <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-white to-sky-50/30">
                      <CardContent className="p-0 text-center">
                        <div className="flex items-center justify-center w-24 h-24 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-2xl mx-auto mb-6">
                          <IconComponent className="h-12 w-12 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                        
                        {step.id === 1 && (
                          <div className="space-y-4">
                            <div className="bg-sky-100 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-sky-600">100+</div>
                              <div className="text-sm text-gray-600">Marques partenaires</div>
                            </div>
                          </div>
                        )}
                        
                        {step.id === 2 && (
                          <div className="space-y-4">
                            <div className="bg-emerald-100 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-emerald-600">500+</div>
                              <div className="text-sm text-gray-600">Points de distribution</div>
                            </div>
                          </div>
                        )}
                        
                        {step.id === 3 && (
                          <div className="space-y-4">
                            <div className="bg-sky-100 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-sky-600">50+</div>
                              <div className="text-sm text-gray-600">Puits construits</div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QR Code Innovation */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Innovation : QR Code Interactif
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Sur chaque bouteille, votre publicité sera accompagnée d'un QR code personnalisé 
                que les consommateurs peuvent scanner avec leur smartphone.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan instantané</h3>
                    <p className="text-gray-600">Le consommateur scanne le QR code avec l'appareil photo de son téléphone</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Redirection automatique</h3>
                    <p className="text-gray-600">Il est automatiquement dirigé vers votre site web, promotion ou offre spéciale</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Interaction mesurable</h3>
                    <p className="text-gray-600">Vous obtenez des statistiques précises sur les scans et les conversions</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-8 rounded-2xl">
                <div className="text-center space-y-6">
                  <div className="text-6xl">📱</div>
                  <h3 className="text-2xl font-bold text-gray-900">QR Code Personnalisé</h3>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="text-8xl">⬛</div>
                    <p className="text-sm text-gray-600 mt-2">Votre QR code unique</p>
                  </div>
                  <p className="text-gray-700 font-medium">
                    → Redirection vers votre site web, promotion, ou landing page
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Le cycle vertueux d'Eau Gratuite
            </h2>
            <p className="text-xl text-gray-600">
              Un modèle économique transparent et solidaire
            </p>
          </div>
          
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {['Financement', 'Distribution', 'Impact'].map((title, index) => (
                <div key={title} className="text-center relative">
                  <div className="bg-white p-6 rounded-xl shadow-lg border border-sky-100">
                    <div className="text-4xl font-bold text-sky-600 mb-2">{index + 1}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600">
                      {index === 0 && 'Les marques investissent dans notre mission'}
                      {index === 1 && 'Vous recevez une eau gratuite et pure'}
                      {index === 2 && 'Ensemble, nous construisons des puits'}
                    </p>
                  </div>
                  {index < 2 && (
                    <ArrowRight className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 h-8 w-8 text-sky-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Prêt à faire partie du mouvement ?
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Rejoignez des centaines d'entreprises qui font déjà confiance à notre plateforme publicitaire solidaire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                >
                  Lancer ma campagne
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/mission">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-sky-200 text-sky-600 hover:bg-sky-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
                >
                  Découvrir notre mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;